<template lang="pug">
DynamicMedia.hero(
  :config="cfg"
  :widths="[768, 1280, 1920]"
  :theme="mediaTheme"
  :preload="768"
)
  .hero-content(:class="{ 'full-width': theme === 'none' }")
    DynamicMedia.logo(:config="logo" v-if="logo")
    Heading.title(level="h2")
      div(v-html="__(title)")
    Heading.subtitle(level="h5" v-if="subtitle")
      div(v-html="__(subtitle)")
    RogueButton.button(:link="link" color="red" v-if="buttonText") {{__(buttonText)}}
</template>
<script setup lang="ts">
import { computed } from 'vue'

interface BackgroundConfig {
  width?: number
  height?: number
  src?: string
}

interface LogoConfig {
  src?: string
  alt?: string
  width?: number
  height?: number
}

interface Product {
  name?: string
  gallery?: Array<{
    link?: string
    label?: string[]
    width?: number
    height?: number
  }>
}

const props = withDefaults(defineProps<{
  background?: BackgroundConfig
  theme?: string
  title?: string
  subtitle?: string
  link?: string
  buttonText?: string
  logo?: LogoConfig
  mobileTheme?: string
  desktopTheme?: string
  productImage?: string
  bgEffect?: string
  product?: Product
}>(), {
  background : () => ({ width: 1600, height: 900 }),
  logo       : () => ({}),
})

const mediaTheme = computed(() => {
  const themes: string[] = []

  if (props.bgEffect) {
    themes.push(props.bgEffect)
  }

  const desktopTheme = props.desktopTheme
  const mobileTheme = props.mobileTheme

  if (desktopTheme) {
    themes.push(`${desktopTheme}-desktop`)
  }

  if (mobileTheme) {
    themes.push(`${mobileTheme}-mobile`)
  }

  if (!themes.length) {
    return undefined
  }

  return themes.join(' ')
})

const cfg = computed(() => {
  if (!props.productImage || props.productImage === 'custom') {
    return props.background
  }

  const gallery = props.product?.gallery || []
  const img = gallery.find((galleryObj) => (galleryObj.label || []).indexOf('header_image') !== -1)
  let src = (img || gallery[0])?.link

  if (props.productImage === 'replaceFirst' && props.background?.src) {
    src = props.background.src
  }

  return {
    src,
    alt        : props.product?.name,
    height     : props.background?.height || img?.height || 800,
    width      : props.background?.width || img?.width || 2000,
    autoHeight : false,
    preload    : true,
  }
})
</script>

<style lang="scss" scoped>

.media-wrap > .content .hero-content {
  h2 {
    font-size: 56px;

    @include mobile() {
      font-size: 36px;
    }

    @include tablet() {
      font-size: 36px;
    }
  }

  h5 {
    font-size: 28px;

    @include mobile() {
      font-size: 18px;
    }

    @include tablet() {
      font-size: 18px;
    }
  }
}

.hero-content {
  color: $color-white;
  max-width: 100%;

  .logo {
    display: inline-block;
    margin-bottom: $space-xs;

    @include all-desktop() {
      width: 350px;
    }

    @include tablet() {
      width: 350px;
    }

    @include mobile() {
      width: 200px;
    }
  }

  .button {
    display: inline-block;
  }

  .subtitle {
    font-size: $size-xs;
    font-style: italic;
  }

  &.full-width {
    width: 100%;
  }

  @include tablet() {
    padding: $space-xxl;
  }
  @include all-desktop() {
    padding: $space-xxl $space-xxxxl;
  }
}
</style>
